import React, { useMemo, useState, useEffect } from "react";
import { useStore } from "../../store/store";
import { useRequiredMsgs } from "../../store/requiredMsgs";
import changeOldInswingToCustom from "./changeOldInswingToCustom";
import {
  jambWithScreen,
  jambWithScreenKerf,
  jambWithScreenSwing,
  jambInswing,
  jambNoScreen,
  jambNoScreenKerf,
  jambFixedSlider,
  jambCentorScreen,
  jambCentorScreenKerf,
  jambSwingCentorScreen,
} from "./defaults";

const oldInswingWood = [
  '5-1/8" Overall Jamb Width',
  '6-1/16" Overall Jamb Width',
  '7" Overall Jamb Width',
  '7-15/16" Overall Jamb Width',
];

function useJambSelector() {
  const addToQuote = useStore((state) => state.addToQuote);

  const doorType = useStore((state) => state.quote.doorType);
  const jamb = useStore((state) => state.quote.jamb);
  const exactConfig = useStore((state) => state.quote.exactConfig);
  const direction = useStore((state) => state.quote.swingDirection);
  const screenType = useStore((state) => state.quote.screenType);
  const addScreen = useStore((state) => state.quote.addScreen);
  const kerf = useStore((state) => state.quote.kerf);
  const material = useStore((state) => state.quote.material);

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const handleErrMsgChange = useStore((state) => state.handleError);

  const [showCustomInput, setShowCustom] = useState(false);

  const fractionValueJamb =
    !jamb || jamb.includes("Overall") || !jamb.split(" ")[1]
      ? ""
      : jamb.split(" ")[1];

  useEffect(() => {
    if (jamb && (!jamb.includes("Overall") || jamb.includes("Custom"))) {
      setShowCustom(true);
    }

    if (doorType !== "Swing" || direction !== "Inswing") return;

    if (
      material === "Clad Exterior/Wood Interior" ||
      !oldInswingWood.includes(jamb || "")
    )
      return;

    const num = changeOldInswingToCustom(jamb || "");
    addToQuote({ jamb: num }, "jamb");
  }, [jamb]);

  useEffect(() => {
    if (doorType !== "Swing" && exactConfig !== "Fixed Inswing") return;

    if (material === "Clad Exterior/Wood Interior") {
      setShowCustom(false);
    }
  }, [doorType, material]);

  const handleClick = (label: string, type: string) => {
    const checked = label === jamb;

    if (checked) {
      if (label.includes("Custom")) {
        setShowCustom(false);
        handleErrMsgChange("jambErr", "");
      }
      addToQuote({ jamb: "" }, "jamb");
    } else {
      addToQuote({ jamb: label }, "jamb");

      if (label.includes("Custom")) {
        material === "All Wood" && setShowCustom(true);
        type !== "inswing" && handleErrMsgChange("jambErr", "");
      } else {
        setShowCustom(false);
        handleErrMsgChange("jambErr", "");
      }

      if (findRequired("Jamb Width")) {
        if (!label.includes("Custom")) {
          removeRequired("Jamb Width", "Custom Jamb Width");
        } else {
          removeRequired("Jamb Width");
        }
      }
    }
  };

  const variant = (label: string): "filled" | "outlined" => {
    var fill: "filled" | "outlined" = "outlined";

    if (label.includes("Custom")) {
      if (showCustomInput) {
        fill = "filled";
      } else {
        fill = "outlined";
      }
    } else {
      if (label === jamb) fill = "filled";
      else fill = "outlined";
    }

    return fill;
  };

  const checkMark = (label: string) => {
    var fill = false;

    if (label.includes("Custom")) {
      if (showCustomInput) fill = true;
      else fill = false;
    } else {
      if (label === jamb) fill = true;
      else fill = false;
    }

    return fill;
  };

  let type = !addScreen ? "noscreen" : "withscreen";

  if (
    ((doorType === "Swing" && direction === "Inswing") ||
      (doorType === "Fixed Panel" && exactConfig === "Fixed Inswing")) &&
    material === "Clad Exterior/Wood Interior"
  ) {
    type = "inswing";
  }

  const options = useMemo(() => {
    if (doorType === "Swing") {
      if (
        direction === "Inswing" &&
        material === "Clad Exterior/Wood Interior"
      ) {
        return jambInswing;
      }

      if (screenType === "Centor Flat Screen/Shade" && addScreen) {
        return jambSwingCentorScreen;
      }

      return !addScreen ? jambNoScreen : jambWithScreenSwing;
    }

    if (
      doorType === "Fixed Panel" &&
      exactConfig === "Fixed Inswing" &&
      material === "Clad Exterior/Wood Interior"
    ) {
      return jambInswing;
    }

    if (doorType === "Fixed Panel" && exactConfig === "Fixed Slider") {
      return jambFixedSlider;
    }

    if (screenType === "Centor Flat Screen/Shade" && addScreen) {
      return !kerf ? jambCentorScreen : jambCentorScreenKerf;
    }

    if (kerf) {
      return !addScreen ? jambNoScreenKerf : jambWithScreenKerf;
    }

    return !addScreen ? jambNoScreen : jambWithScreen;
  }, [doorType, direction, exactConfig, material, addScreen, kerf]);

  return {
    options,
    type,
    checkMark,
    variant,
    handleClick,
    fractionValueJamb,
    doorType,
    direction,
    showCustomInput,
    jamb,
  };
}

export default useJambSelector;
